/**
 * Basemap definitions, as data. Each basemap is a set of sources and layers that the adapter places beneath
 * every project layer. Ids are prefixed so they never collide with project layer ids.
 *
 * The default is aerial imagery: NSW Government (Department of Customer Service, Spatial Services) imagery under
 * CC BY 4.0. The data plane relays it from the NSW server (the open GeoServices tile protocol, not an Esri
 * service), so the browser only ever talks to our own data plane: some networks cannot reach the NSW server
 * directly. It covers NSW only, so the source is bounded to the state and nothing is requested outside it. The
 * others are NSW's too, relayed the same way: the topographic map, NSW's colour vector base map (Streets), its
 * greyscale one (Light grey) and the greyscale turned dark by the data plane (Dark grey). The OpenStreetMap raster
 * entry is for local development only (the OSM tile usage policy does not allow production traffic).
 */

export const BASEMAP_PREFIX = 'basemap:';

export interface BasemapDefinition {
  id: string;
  title: string;
  /**
   * Source specs keyed by source id (already prefixed). A tile URL that starts with `/` is a data-plane path: the
   * adapter fetches it from the data plane with the viewer's data token.
   */
  sources: Readonly<Record<string, Readonly<Record<string, unknown>>>>;
  /** Layer specs, bottom first (ids already prefixed). */
  layers: readonly Readonly<Record<string, unknown>>[];
  devOnly: boolean;
  /** Basemap to switch to when this one's tiles cannot be reached at all from the viewer's network. */
  fallback?: string;
  /**
   * A data-plane path to a reference overlay's style (labels and roads over imagery, as a hybrid basemap has).
   * The adapter adds its layers above project layers and below tool overlays, with its glyphs and sprite.
   */
  overlay?: string;
  /** A data-plane path to a whole vector style drawn as the basemap, beneath the project layers. */
  style?: string;
  /**
   * Raster tiles standing in for a vector basemap where a picture is wanted: its gallery thumbnail and the globe,
   * which drapes rasters only. `look` tones them as the vector basemap is toned.
   */
  raster?: { tiles: string; maxzoom: number; look?: 'grey' | 'dark' };
  /** The credit of a basemap whose sources come with its style. */
  attribution?: string;
}

/** The reference overlay's two parts and how its roads are drawn (the Basemap tab's switches). */
export type RoadTheme = 'classic' | 'dark' | 'amber';
export interface ReferenceOptions {
  /** Place names and the state border. */
  places: boolean;
  /** Roads and railways, with their labels. */
  roads: boolean;
  /** Classic is the overlay's own colours; dark and amber recolour the roads, the edge darker than the road. */
  roadTheme: RoadTheme;
  /** The roads' opacity, in percent (20 to 100). */
  roadFade: number;
}

export const DEFAULT_REFERENCE: ReferenceOptions = { places: true, roads: true, roadTheme: 'classic', roadFade: 100 };

/** Road and edge colours of the dark and amber themes, as the Esri edition's Layers Pro draws them. */
export const ROAD_THEMES: Readonly<Record<Exclude<RoadTheme, 'classic'>, { road: string; edge: string }>> = {
  dark: { road: '#5d666f', edge: '#14181c' },
  amber: { road: '#ffb845', edge: '#452f10' },
};

/** Which part of the overlay an upstream source layer belongs to; null for a layer it leaves as it is. */
export function referencePart(sourceLayer: unknown): 'places' | 'roads' | null {
  if (typeof sourceLayer !== 'string') return null;
  if (/^(Road|AggregatedWay|Railway)/.test(sourceLayer)) return 'roads';
  if (/^(PlacePoint|Suburb|Border)/.test(sourceLayer)) return 'places';
  return null;
}

/** A road line's colour under a theme, as a road or its edge. Classic keeps its own. */
export function roadColour(own: unknown, theme: RoadTheme, edge: boolean): unknown {
  if (theme === 'classic') return own;
  return edge ? ROAD_THEMES[theme].edge : ROAD_THEMES[theme].road;
}

const lightness = (colour: unknown): number => {
  const m = typeof colour === 'string' ? /^#([0-9a-f]{6})$/i.exec(colour.trim()) : null;
  if (!m) return 1;
  const n = parseInt(m[1]!, 16);
  const c = [n >> 16, (n >> 8) & 255, n & 255];
  return (Math.max(...c) + Math.min(...c)) / 510;
};

/**
 * The overlay's road lines that are edges. The overlay draws a cased road as lines of one name and a number
 * (`RoadAgg_M/AccessWay/0` over `.../1`): the lightest of them is the road, the rest its edge. A road drawn as one
 * line is a road.
 */
export function roadEdges(layers: readonly Readonly<Record<string, unknown>>[]): Set<string> {
  const groups = new Map<string, { id: string; light: number }[]>();
  for (const l of layers) {
    if (l['type'] !== 'line' || referencePart(l['source-layer']) !== 'roads') continue;
    const id = String(l['id']);
    const key = id.replace(/\/\d+$/, '');
    const paint = (l['paint'] ?? {}) as Record<string, unknown>;
    groups.set(key, [...(groups.get(key) ?? []), { id, light: lightness(paint['line-color']) }]);
  }
  const edges = new Set<string>();
  for (const g of groups.values()) {
    if (g.length < 2) continue;
    const road = g.reduce((a, b) => (b.light > a.light ? b : a));
    for (const l of g) if (l !== road) edges.add(l.id);
  }
  return edges;
}

export const BACKGROUND_COLOR = '#f2f1ec';
export const IMAGERY_FLOOR_COLOR = '#0e1a22';

const background = {
  id: `${BASEMAP_PREFIX}background`,
  type: 'background',
  paint: { 'background-color': BACKGROUND_COLOR },
} as const;

/** NSW imagery as the data plane relays it, in XYZ order (the relay maps it to the NSW server's {z}/{y}/{x}). */
export const NSW_IMAGERY_TILES = '/v1/basemaps/imagery/tiles/{z}/{x}/{y}';
/** NSW's topographic map, cached to z16 (over-zoomed beyond). */
export const NSW_TOPO_TILES = '/v1/basemaps/topo/tiles/{z}/{x}/{y}';
/** NSW's cached base map: the vector basemaps' stand-in picture (thumbnail, globe). */
export const NSW_BASE_TILES = '/v1/basemaps/base/tiles/{z}/{x}/{y}';
const NSW_CREDIT = '© State of New South Wales (Spatial Services, Department of Customer Service NSW), CC BY 4.0';
/** NSW's roads, railways and place names over the imagery: a style the data plane composes from NSW's own. */
export const NSW_LABELS_STYLE = '/v1/basemaps/labels/style.json';
/** Glyphs for every label on the map: the labels overlay's fonts and the project label font (Public Sans). */
export const DEFAULT_GLYPHS = '/v1/basemaps/labels/glyphs/{fontstack}/{range}.pbf';
export const NSW_IMAGERY_ATTRIBUTION = 'Imagery © Department of Customer Service NSW (Spatial Services), CC BY 4.0';
/** [west, south, east, north]: NSW plus a small margin. */
export const NSW_BOUNDS = [140.9, -37.6, 153.7, -28.1] as const;

export const BASEMAPS: readonly BasemapDefinition[] = [
  {
    id: 'imagery',
    fallback: 'osm-dev',
    overlay: NSW_LABELS_STYLE,
    title: 'Imagery',
    sources: {
      [`${BASEMAP_PREFIX}imagery`]: {
        type: 'raster',
        tiles: [NSW_IMAGERY_TILES],
        tileSize: 256,
        bounds: [...NSW_BOUNDS],
        // Coverage below z19 is patchy in rural areas (404s); deeper zooms over-zoom the z19 tiles instead.
        maxzoom: 19,
        attribution: NSW_IMAGERY_ATTRIBUTION,
      },
    },
    layers: [
      // Dark floor outside the NSW coverage (mostly ocean), so the imagery's edge does not show as a pale band.
      { ...background, paint: { 'background-color': IMAGERY_FLOOR_COLOR } },
      { id: `${BASEMAP_PREFIX}imagery`, type: 'raster', source: `${BASEMAP_PREFIX}imagery` },
    ],
    devOnly: false,
  },
  {
    id: 'topo',
    title: 'Topographic',
    sources: {
      [`${BASEMAP_PREFIX}topo`]: {
        type: 'raster',
        tiles: [NSW_TOPO_TILES],
        tileSize: 256,
        bounds: [...NSW_BOUNDS],
        maxzoom: 16,
        attribution: NSW_CREDIT,
      },
    },
    layers: [background, { id: `${BASEMAP_PREFIX}topo`, type: 'raster', source: `${BASEMAP_PREFIX}topo` }],
    devOnly: false,
  },
  {
    id: 'streets',
    title: 'Streets',
    sources: {},
    layers: [{ ...background, paint: { 'background-color': '#f7f6f2' } }],
    style: '/v1/basemaps/streets/style.json',
    raster: { tiles: NSW_BASE_TILES, maxzoom: 19 },
    attribution: NSW_CREDIT,
    devOnly: false,
  },
  {
    id: 'light',
    title: 'Light grey',
    sources: {},
    layers: [{ ...background, paint: { 'background-color': '#f4f4f4' } }],
    style: '/v1/basemaps/light/style.json',
    raster: { tiles: NSW_BASE_TILES, maxzoom: 19, look: 'grey' },
    attribution: NSW_CREDIT,
    devOnly: false,
  },
  {
    id: 'dark',
    title: 'Dark grey',
    sources: {},
    layers: [{ ...background, paint: { 'background-color': '#1b1e22' } }],
    style: '/v1/basemaps/dark/style.json',
    raster: { tiles: NSW_BASE_TILES, maxzoom: 19, look: 'dark' },
    attribution: NSW_CREDIT,
    devOnly: false,
  },
  {
    id: 'none',
    title: 'None',
    sources: {},
    layers: [background],
    devOnly: false,
  },
  {
    id: 'osm-dev',
    title: 'OpenStreetMap (development only)',
    sources: {
      [`${BASEMAP_PREFIX}osm`]: {
        type: 'raster',
        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
        tileSize: 256,
        maxzoom: 19,
        attribution: '© OpenStreetMap contributors',
      },
    },
    layers: [background, { id: `${BASEMAP_PREFIX}osm`, type: 'raster', source: `${BASEMAP_PREFIX}osm` }],
    devOnly: true,
  },
];

export const DEFAULT_BASEMAP_ID = 'imagery';

/** Unknown ids fall back to the plain background (None) so the map always has a floor. */
export function getBasemap(id: string): BasemapDefinition {
  return BASEMAPS.find((b) => b.id === id) ?? (BASEMAPS.find((b) => b.id === 'none') as BasemapDefinition);
}

/** A tile URL the adapter must fetch from the data plane, with the data token. */
export function isDataPlanePath(url: string): boolean {
  return url.startsWith('/');
}

export function isBasemapId(id: string): boolean {
  return id.startsWith(BASEMAP_PREFIX);
}
